import { useCallback, useEffect, useMemo, useState } from 'react';
import { LockClosedIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api, { getErrorMessage } from '@/services/api';
import { Badge, Button, EmptyState, Input, LoadingSpinner, Select } from '@/components/common';
import { formatDateTime } from '@/utils/format';

/**
 * Board #90 (second half) — the country admin's private remarks ABOUT a broker.
 *
 * Backed by `brokers.BrokerNote` and served ONLY by
 * `/brokers/broker-notes/`, whose permission class is `IsCountryAdmin`. The
 * broker the remark is about — its admins, employees and POSPs — is refused at
 * the permission layer, which is the whole point of the feature and the reason
 * these notes are not stored in `Broker.settings` (that blob is GET-able *and*
 * PATCH-able by the broker's own admin).
 *
 * Two rules this panel keeps, because this codebase has repeatedly broken them:
 *
 * 1. A failed request is never dressed up as an empty one. If the list call
 *    fails the panel shows the server's reason; it does NOT fall through to
 *    "No notes yet", which would read as "this broker is clean".
 * 2. Nothing claims success before the server has confirmed it. The success
 *    toast and the list refresh both happen after the 2xx, never alongside the
 *    request; on a failure the typed draft is kept on screen so it is not lost.
 */

export interface BrokerNote {
  id: string;
  broker: string;
  broker_name?: string;
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  is_pinned: boolean;
  created_by: string | null;
  created_by_name: string | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

interface Paginated<T> {
  count: number;
  results: T[];
}

const ENDPOINT = '/brokers/broker-notes/';

/** One request's worth of notes. `count` tells us if the server has more. */
const PAGE_SIZE = 100;

const PRIORITIES: { value: BrokerNote['priority']; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const PRIORITY_VARIANT: Record<BrokerNote['priority'], 'default' | 'info' | 'warning' | 'error'> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  critical: 'error',
};

interface DraftState {
  title: string;
  content: string;
  priority: BrokerNote['priority'];
  is_pinned: boolean;
}

const EMPTY_DRAFT: DraftState = { title: '', content: '', priority: 'medium', is_pinned: false };

interface BrokerNotesPanelProps {
  brokerId: string;
  /** Only used in prose ("No notes yet for X"); the API is keyed by id. */
  brokerName?: string;
  className?: string;
}

export function BrokerNotesPanel({ brokerId, brokerName, className }: BrokerNotesPanelProps) {
  const [notes, setNotes] = useState<BrokerNote[]>([]);
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await api.get<Paginated<BrokerNote>>(ENDPOINT, {
        params: { broker: brokerId, page_size: PAGE_SIZE },
      });
      const results = Array.isArray(resp.data?.results) ? resp.data.results : [];
      setNotes(results);
      setCount(typeof resp.data?.count === 'number' ? resp.data.count : results.length);
    } catch (err) {
      // Deliberately clear the rows AND remember why. An empty list with no
      // explanation is the one thing this panel must never render after a
      // failure — see the class docstring.
      setNotes([]);
      setCount(null);
      setLoadError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [brokerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (note: BrokerNote) => {
    setEditingId(note.id);
    setDraft({
      title: note.title ?? '',
      content: note.content ?? '',
      priority: note.priority,
      is_pinned: Boolean(note.is_pinned),
    });
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setFormError(null);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const content = draft.content.trim();
    if (!content) {
      // Caught here as well as server-side: a blank note is not worth a round
      // trip, and the server's 400 would arrive as a generic field error.
      setFormError('A note needs some content.');
      return;
    }

    const payload = {
      broker: brokerId,
      title: draft.title.trim(),
      content,
      priority: draft.priority,
      is_pinned: draft.is_pinned,
    };

    setSaving(true);
    setFormError(null);
    try {
      if (editingId) {
        await api.patch(`${ENDPOINT}${editingId}/`, payload);
      } else {
        await api.post(ENDPOINT, payload);
      }
      // Only past this line is anything true: the row exists.
      closeForm();
      toast.success(editingId ? 'Note updated.' : 'Note added.');
      await load();
    } catch (err) {
      // Keep the form open with the text still in it.
      setFormError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (note: BrokerNote) => {
    setDeletingId(note.id);
    try {
      await api.delete(`${ENDPOINT}${note.id}/`);
      setConfirmingId(null);
      toast.success('Note deleted.');
      await load();
    } catch (err) {
      // The row stays on screen. Removing it locally would show a deletion
      // that did not happen.
      setConfirmingId(null);
      toast.error(getErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  };

  const hasMore = useMemo(
    () => count !== null && count > notes.length,
    [count, notes.length]
  );

  return (
    <section className={className} aria-labelledby="broker-notes-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            id="broker-notes-heading"
            className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white"
          >
            <LockClosedIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            Internal Notes
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Country and super admins only — these notes are not visible to the broker they are
            about.
          </p>
        </div>
        {!formOpen && (
          <Button
            type="button"
            size="sm"
            leftIcon={<PlusIcon className="h-4 w-4" />}
            onClick={openCreate}
          >
            Add note
          </Button>
        )}
      </div>

      {formOpen && (
        <form
          onSubmit={submit}
          className="mt-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
        >
          <Input
            id="broker-note-title"
            name="title"
            label="Title (optional)"
            value={draft.title}
            maxLength={255}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />

          <div className="w-full">
            <label
              htmlFor="broker-note-content"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Note
            </label>
            <textarea
              id="broker-note-content"
              name="content"
              rows={4}
              value={draft.content}
              onChange={(e) => setDraft({ ...draft, content: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors duration-200 hover:border-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40">
              <Select
                id="broker-note-priority"
                name="priority"
                label="Priority"
                options={PRIORITIES}
                value={draft.priority}
                onChange={(e) =>
                  setDraft({ ...draft, priority: e.target.value as BrokerNote['priority'] })
                }
              />
            </div>
            <label
              htmlFor="broker-note-pinned"
              className="mb-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200"
            >
              <input
                id="broker-note-pinned"
                name="is_pinned"
                type="checkbox"
                checked={draft.is_pinned}
                onChange={(e) => setDraft({ ...draft, is_pinned: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Pin to top
            </label>
          </div>

          {formError && (
            <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm">
              <p className="text-red-700">{formError}</p>
            </div>
          )}

          <div className="flex gap-2">
            <Button type="submit" size="sm" isLoading={saving}>
              Save note
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={closeForm} disabled={saving}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {loading && (
        <div className="py-8">
          <LoadingSpinner />
        </div>
      )}

      {!loading && loadError && (
        <div
          role="alert"
          className="mt-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          <p className="font-semibold">Notes could not be loaded.</p>
          <p className="mt-1">{loadError}</p>
        </div>
      )}

      {!loading && !loadError && notes.length === 0 && (
        <EmptyState
          title="No notes yet"
          description={
            brokerName
              ? `Nothing has been recorded about ${brokerName}.`
              : 'Nothing has been recorded about this broker.'
          }
        />
      )}

      {!loading && !loadError && notes.length > 0 && (
        <div className="mt-4 space-y-3">
          {hasMore && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing {notes.length} of {count} notes.
            </p>
          )}
          {notes.map((note) => (
            <article
              key={note.id}
              className="rounded-lg card-surface border border-gray-200 p-4 dark:border-gray-700"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {note.title && (
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {note.title}
                    </h3>
                  )}
                  <Badge size="sm" variant={PRIORITY_VARIANT[note.priority] ?? 'default'}>
                    {PRIORITIES.find((p) => p.value === note.priority)?.label ?? note.priority}
                  </Badge>
                  {note.is_pinned && (
                    <Badge size="sm" variant="primary">
                      Pinned
                    </Badge>
                  )}
                </div>

                {confirmingId === note.id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 dark:text-gray-300">Delete this note?</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="danger"
                      isLoading={deletingId === note.id}
                      onClick={() => void remove(note)}
                    >
                      Confirm delete
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmingId(null)}
                    >
                      Keep
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      leftIcon={<PencilSquareIcon className="h-4 w-4" />}
                      onClick={() => openEdit(note)}
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      leftIcon={<TrashIcon className="h-4 w-4" />}
                      onClick={() => setConfirmingId(note.id)}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              </div>

              <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">
                {note.content}
              </p>

              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                {note.created_by_name || 'Unknown author'} · {formatDateTime(note.created_at)}
              </p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
